/**
 * Client-side canonical-constants mirror for the recovery flow.
 *
 * **Source of truth:** `@shn/phg-projection-service`'s
 * `src/recovery/recovery-cryptographic-mechanism.ts`
 * ([PR #290](https://github.com/SmartHealthNetwork/SHN2026/pull/290))
 * — GC-disposed per [Issue #48 disposition](https://github.com/SmartHealthNetwork/SHN2026/issues/48#issuecomment-4393749031)
 * Q1/Q3/Q4. Changes to canonical values require GC re-disposition.
 *
 * **Why mirror, not direct import.** `phg-projection-service` is a
 * server-side workspace with `@aws-sdk/client-dynamodb` + S3 transitive
 * deps that are NodeJS-only — direct import into this browser-bundled
 * client workspace would break the bundle. The mirror approach
 * preserves the substrate-vs-conventional split discipline per
 * `feedback_substrate_conventional_split_discipline`: the canonical
 * source is real substrate code; this mirror is a bounded conventional
 * convenience structurally enforced by the binding test.
 *
 * **Drift detection.** `test/recovery/canonical-constants-binding.test.ts`
 * asserts these values match the server-side canonical exactly. A
 * failing test indicates either (a) the server-side canonical changed
 * without GC re-disposition surface (unexpected; report to DEV3 +
 * VP Eng) or (b) this mirror is stale (refresh from server canonical).
 *
 * **Pattern B no-parallel-rendering-surface discipline preserved**
 * per [`feedback_pattern_b_no_parallel_rendering_surface`](MEMORY):
 * client uses these values for read-only display surface confirmation
 * + test fixture parity. Recovery enforcement remains backend-side
 * per `recovery_flow_design.md` §3.3.
 *
 * **Doctrinal anchors preserved (mirrored from server canonical):**
 * - NO SHT BACKDOOR (Doctrine v1.3 §6/§8 A.4/B.1): SHT-held share
 *   is one-of-N; t=3 still requires 2 patient-controlled shares + the
 *   recovery passphrase (separate factor; not held by SHT).
 * - Recovery UX honesty (Doctrine v1.3 §8 B.3 ELEVATE per
 *   `feedback_phg_recovery_ux_honesty`): "content unrecoverable"
 *   framing must survive UX simplification.
 * - Recovery ≠ erasure (ADR-024 §2.7.5).
 */
/**
 * Tier → required-layers mapping per
 * [GC Issue #48 disposition](https://github.com/SmartHealthNetwork/SHN2026/issues/48#issuecomment-4393749031)
 * Q3 + PFO §4.2 risk-tier matrix. Mirrors the server canonical.
 */
export const TIER_REQUIRED_LAYERS = {
    'low-risk': ['knowledge', 'possession'],
    'medium-risk': ['knowledge', 'possession', 'ial2-reverify'],
    'high-risk': ['possession', 'ial2-reverify', 'in-person'],
    'highest-risk': ['possession', 'ial2-reverify', 'in-person', 'trusted-other'],
};
/**
 * Tier → cool-off duration in seconds per
 * [GC Issue #48 disposition](https://github.com/SmartHealthNetwork/SHN2026/issues/48#issuecomment-4393749031)
 * Q3 + PFO §4.2 risk-tier matrix. Mirrors the server canonical.
 *
 * Substrate-time-source authority: server returns `coolOffUntil`
 * (absolute timestamp) computed from server clock + this duration;
 * client polls + displays countdown; backend remains time-source
 * authority per `recovery_flow_design.md` §3.3.
 */
export const TIER_COOL_OFF_SECONDS = {
    'low-risk': 0,
    'medium-risk': 86_400,
    'high-risk': 259_200,
    'highest-risk': 604_800,
};
/**
 * Recovery threshold-scheme defaults per
 * [GC Issue #48 disposition](https://github.com/SmartHealthNetwork/SHN2026/issues/48#issuecomment-4393749031)
 * Q3 + scope memo §5.2. Mirrors the server canonical.
 *
 * **N=5 recovery elements; t=3 threshold reconstruction.** Per GC
 * disposition the 5 element categories are:
 *   (a) recovery codes issued at registration
 *   (b) backup authentication device
 *   (c) recovery email or recovery phone
 *   (d) trusted-others enrolled per PFO §4.3
 *   (e) SHT-held recovery share
 *
 * Threshold property: SHT-held share is one-of-N; cannot independently
 * complete reconstruction even if compromised — t=3 still requires 2
 * patient-controlled shares AND the recovery passphrase.
 *
 * Per-patient threshold parameter variability deferred to v1.5+
 * patient-advocacy-consultation work with GC sign-off.
 */
export const RECOVERY_THRESHOLD_DEFAULTS = {
    N: 5,
    t: 3,
};
/**
 * PBKDF2 iterations canonical per `@shn/envelope-crypto` (OWASP 2023
 * 600,000-iteration guidance). Mirrors the server canonical re-export
 * at `recovery-cryptographic-mechanism.DEFAULT_PBKDF2_ITERATIONS`.
 *
 * Client uses this value for the recovery-passphrase KDF derivation
 * at Phase B `submitPassphrase`. Server returns same value via
 * `FinalizeRecoveryResponse.pbkdf2Iterations` for byte-for-byte
 * derivation parity at unwrap time.
 */
export const DEFAULT_PBKDF2_ITERATIONS = 600_000;
//# sourceMappingURL=canonical-constants.js.map